import { inject, Injectable } from '@angular/core';
import { flatMap } from '@sama/micro-dash';
import { Dictionary, forOwn, keyBy } from 'lodash';
import { lastValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { UuidService } from '../../../cross-team-shared/misc-services/uuid.service';
import { WorkspaceConfigService } from '../../../shared/misc-services/workspace-config.service';
import {
  AttributeByClass,
  Attributes,
  TaxonomyItem,
} from '../../../team-apis/core/taxonomy';
import {
  AttributeExternal,
  TaxonomyExternal,
  TaxonomyItemExternal,
} from '../../../team-apis/otterdam/taxonomy';
import { TaskWorkspaceStore } from '../../h3ml/state/task-workspace-store';
import { AbstractResource } from '../../http/abstract-resource';

@Injectable()
export class TaxonomyLoadingService extends AbstractResource {
  private store = inject(TaskWorkspaceStore);
  private workspaceConfigService = inject(WorkspaceConfigService);
  private uuidService = inject(UuidService);

  async load() {
    if (!this.store.isUsingObjects()) {
      return;
    }

    const taxonomy = await lastValueFrom(
      this.request$<TaxonomyExternal>(
        'GET',
        taxonomyPath(this.workspaceConfigService.projectId),
        {
          cache: true,
          customErrorMessage: 'failed to load taxonomy.',
          tokenScope: 'access_token',
        },
      ),
    );

    this.storeTaxonomy(taxonomy);
  }

  private storeTaxonomy(taxonomy: TaxonomyExternal) {
    const sceneAttributes = keyBy(taxonomy.scene, 'name');
    deleteNullValues(sceneAttributes);
    const objectTaxonomy = this.convertToObjectTaxonomy(taxonomy.objects);
    const attributesByClass = keyBy(
      this.buildObjectAttributes(objectTaxonomy, {}, []),
      'name',
    );

    this.store('taxonomy').assign({
      sceneAttributes,
      objectTaxonomy,
      attributesByClass,
      type: taxonomy.type,
    });
  }

  private convertToObjectTaxonomy(
    taxonomyItems: TaxonomyItemExternal[],
    parentColor?: string,
  ): TaxonomyItem[] {
    return taxonomyItems
      .filter((item) => isExternalTaxonomyClass(item) || item.children.length)
      .map((itemExternal) => {
        const commonProperties = {
          // generating a unique id to simplify class selector code (__id is not
          // used for anything else, and backend allows a class group to have
          // the same id as a class)
          __id: this.uuidService.getUuidV4(),
          attributes: keyBy(itemExternal.attributes, 'name'),
          color: itemExternal.color || parentColor,
        };
        const converted = isExternalTaxonomyClass(itemExternal)
          ? {
              ...itemExternal,
              ...commonProperties,
            }
          : {
              ...itemExternal,
              ...commonProperties,
              children: this.convertToObjectTaxonomy(
                itemExternal.children,
                commonProperties.color,
              ),
            };
        deleteNullValues(converted.attributes);
        return converted as TaxonomyItem;
      });
  }

  private buildObjectAttributes(
    taxonomyItems: TaxonomyItem[],
    parentAttributes: Attributes,
    parentPath: string[],
  ): AttributeByClass[] {
    return flatMap(taxonomyItems, (item) => {
      const attributes = {
        ...item.attributes,
        ...parentAttributes,
      };

      if (isClassTaxonomy(item)) {
        return {
          name: item.name,
          attributes,
          path: [...parentPath, item.__id],
          label: item.label,
        };
      }

      return this.buildObjectAttributes(item.children, attributes, [
        ...parentPath,
        item.__id,
      ]);
    });
  }
}

export function isExternalTaxonomyClass(taxonomyItem: TaxonomyItemExternal) {
  return taxonomyItem.__type === 'class';
}

export function isClassTaxonomy(taxonomyItem: TaxonomyItem) {
  return taxonomyItem.__type === 'class';
}

export function taxonomyPath(projectId: number) {
  return `${environment.settingsServiceBaseUrl}/settings/taxonomy/${projectId}?expand=true`;
}

function deleteNullValues(
  attributes: Dictionary<AttributeExternal | undefined>,
): asserts attributes is Attributes {
  const embeddedSettingKeys = ['shapes'] as const;
  forOwn(attributes, (attribute) => {
    for (const key of embeddedSettingKeys) {
      if (attribute && attribute[key] === null) {
        attribute[key] = undefined;
      }
    }
  });
}
